import Decimal from 'decimal.js'
import type { UserReceivingPlan, ReceivingPlanFee } from './user-receiving-plan'
import { planName, planPercent } from './user-receiving-plan'

export type PlanValidationIssue = { currency: string; field: string; message: string }

export function planValidationIssues(
  plan: UserReceivingPlan,
  positiveServiceFees = false,
): PlanValidationIssue[] {
  if (plan.kind !== 'CUSTOM') return []
  const errors: PlanValidationIssue[] = []
  const add = (currency: string, field: string, message: string) =>
    errors.push({ currency, field, message })
  if (plan.serviceFee) {
    if (!/^\d{1,2}(\.\d{1,3})?$/.test(plan.serviceFee.percentage))
      add(
        'USD',
        'servicePercentage',
        positiveServiceFees && /[^\d.-]/.test(plan.serviceFee.percentage)
          ? 'Informe apenas números no percentual do serviço.'
          : positiveServiceFees
            ? 'Informe um percentual de serviço de 0,001 a 99,999%.'
            : 'Informe um percentual de serviço de 0 a 99,999%.',
      )
    if (!/^\d{1,9}(\.\d{1,2}0?)?$/.test(plan.serviceFee.fixedUsd))
      add('USD', 'serviceFixed', 'Informe uma parcela fixa válida, com até dois decimais em dólar.')
    if (positiveServiceFees) {
      if (/^0+(\.0+)?$/.test(plan.serviceFee.percentage))
        add('USD', 'servicePercentage', 'O percentual do serviço deve ser maior que zero.')
      if (/^0+(\.0+)?$/.test(plan.serviceFee.fixedUsd))
        add('USD', 'serviceFixed', 'A parcela fixa do serviço deve ser maior que zero.')
    }
  }
  for (const [currency, value] of Object.entries(plan.withdrawalFees ?? {})) {
    const decimals = ['CLP', 'JPY', 'PYG'].includes(currency) ? 0 : 2
    if (
      !/^\d{1,9}(\.\d{1,3})?$/.test(value) ||
      !new Decimal(value).equals(new Decimal(value).toDecimalPlaces(decimals))
    )
      add(
        currency,
        'withdrawalFees',
        `${currency}: informe uma taxa configurada válida para as casas decimais da moeda.`,
      )
  }
  if (plan.receivingDays !== 5)
    add('BRL', 'receivingDays', 'O prazo de recebimento do cartão é fixo.')
  if (
    !/^\d{1,9}(\.\d{1,3})?$/.test(plan.withdrawalFeeBrl) ||
    !new Decimal(plan.withdrawalFeeBrl || 0).equals(
      new Decimal(plan.withdrawalFeeBrl || 0).toDecimalPlaces(2),
    )
  )
    add('BRL', 'withdrawalFees', 'Taxa configurada: informe um valor válido em reais.')
  for (const fee of plan.fees) {
    for (const field of ['nationalReserveDays', 'globalReserveDays'] as const)
      if (!Number.isInteger(fee[field]) || fee[field] < 0 || fee[field] > 3650)
        add(fee.currency, field, `${fee.currency}: confira os dias de liberação da reserva.`)
    for (const field of ['nationalReservePercentage', 'globalReservePercentage'] as const) {
      if (/^\d{1,2}(\.\d{1,3})?$/.test(fee[field]) && !new Decimal(fee[field]).isInteger())
        add(fee.currency, field, `${fee.currency}: a reserva deve ser um percentual inteiro.`)
    }
    if (fee.currency === 'BRL') {
      if (
        !/^\d{1,2}(\.\d{1,3})?$/.test(fee.nationalReservePercentage) ||
        new Decimal(fee.nationalReservePercentage || 0).lt(1) ||
        new Decimal(fee.nationalReservePercentage || 0).gt(100)
      )
        add(
          'BRL',
          'nationalReservePercentage',
          positiveServiceFees && /[^\d.-]/.test(fee.nationalReservePercentage)
            ? 'Informe apenas números no percentual da reserva.'
            : 'BRL: a reserva deve ser um percentual inteiro de 1 a 100.',
        )
      if (fee.nationalReserveDays < 3 || fee.nationalReserveDays > 180)
        add('BRL', 'nationalReserveDays', 'BRL: a reserva deve durar de 3 a 180 dias.')
    }
    for (const [rate, reserve] of [
      ['nationalFeePercentage', 'nationalReservePercentage'],
      ['pixFeePercentage', 'nationalReservePercentage'],
      ['internationalFeePercentage', 'globalReservePercentage'],
    ] as const) {
      if (
        ![fee[rate], fee[reserve]].every((value) => /^\d{1,2}(\.\d{1,3})?$/.test(value)) ||
        new Decimal(fee[rate] || 0).plus(fee[reserve] || 0).gte(100)
      ) {
        add(
          fee.currency,
          !/^\d{1,2}(\.\d{1,3})?$/.test(fee[reserve]) ? reserve : rate,
          `${fee.currency}: taxa e reserva devem somar menos que 100%.`,
        )
      }
    }
    for (const key of ['nationalFixedFee', 'internationalFixedFee'] as const) {
      const currency = key === 'nationalFixedFee' ? 'BRL' : fee.currency
      const decimals = new Intl.NumberFormat('pt-BR', {
        style: 'currency',
        currency,
      }).resolvedOptions().maximumFractionDigits
      if (
        !/^\d{1,9}(\.\d{1,3})?$/.test(fee[key]) ||
        !new Decimal(fee[key]).equals(new Decimal(fee[key]).toDecimalPlaces(decimals))
      )
        add(
          fee.currency,
          key,
          `${currency}: informe uma taxa fixa válida para as casas decimais da moeda.`,
        )
    }
  }
  return errors
}

export function planValidation(plan: UserReceivingPlan): string[] {
  return [...new Set(planValidationIssues(plan).map((issue) => issue.message))]
}

type Change = { label: string; before: string; after: string }
export function planChanges(before: UserReceivingPlan, after: UserReceivingPlan): Change[] {
  const changes: Change[] = []
  const add = (label: string, old: string, next: string) => {
    if (old !== next) changes.push({ label, before: old, after: next })
  }
  add('Plano', planName(before), planName(after))
  const cash = (value: string, currency: string) =>
    new Intl.NumberFormat('pt-BR', { style: 'currency', currency }).format(Number(value))
  add(
    'Percentual do serviço',
    planPercent(before.serviceFee?.percentage ?? '9.9'),
    planPercent(after.serviceFee?.percentage ?? '9.9'),
  )
  add(
    'Parcela fixa do serviço (USD)',
    cash(before.serviceFee?.fixedUsd ?? '1.49', 'USD'),
    cash(after.serviceFee?.fixedUsd ?? '1.49', 'USD'),
  )
  add('Taxa configurada', cash(before.withdrawalFeeBrl, 'BRL'), cash(after.withdrawalFeeBrl, 'BRL'))
  for (const currency of new Set([
    ...Object.keys(before.withdrawalFees ?? {}),
    ...Object.keys(after.withdrawalFees ?? {}),
  ])) {
    const key = currency as keyof NonNullable<UserReceivingPlan['withdrawalFees']>
    const oldFee = before.withdrawalFees?.[key]
    const newFee = after.withdrawalFees?.[key]
    const fallback = 'Valor configurado padrão da moeda'
    add(
      `Taxa configurada (${currency})`,
      oldFee === undefined ? fallback : cash(oldFee, currency),
      newFee === undefined ? fallback : cash(newFee, currency),
    )
  }
  const fields: Array<{
    key: keyof Omit<ReceivingPlanFee, 'currency'>
    label: string
    format: 'percent' | 'days' | 'money'
    national: boolean
  }> = [
    { key: 'nationalFeePercentage', label: 'Cartão em reais', format: 'percent', national: true },
    { key: 'nationalFixedFee', label: 'Taxa fixa em reais', format: 'money', national: true },
    { key: 'internationalFeePercentage', label: 'Cartão', format: 'percent', national: false },
    { key: 'internationalFixedFee', label: 'Taxa fixa', format: 'money', national: false },
    { key: 'pixFeePercentage', label: 'Pix', format: 'percent', national: true },
    {
      key: 'nationalReservePercentage',
      label: 'Reserva nacional',
      format: 'percent',
      national: true,
    },
    {
      key: 'nationalReserveDays',
      label: 'Liberação da reserva nacional',
      format: 'days',
      national: true,
    },
    {
      key: 'globalReservePercentage',
      label: 'Reserva internacional',
      format: 'percent',
      national: false,
    },
    {
      key: 'globalReserveDays',
      label: 'Liberação da reserva internacional',
      format: 'days',
      national: false,
    },
  ]
  for (const fee of after.fees) {
    const previous = before.fees.find((item) => item.currency === fee.currency)
    if (!previous) continue
    for (const field of fields) {
      if (field.national !== (fee.currency === 'BRL')) continue
      const format = (value: string | number) =>
        field.format === 'days'
          ? `${value} dias`
          : field.format === 'percent'
            ? planPercent(String(value))
            : cash(String(value), fee.currency)
      add(`${field.label} (${fee.currency})`, format(previous[field.key]), format(fee[field.key]))
    }
  }
  return changes
}
